<template>
    <div class="image-library-panel">
        <label>{{ i18n("voucherDesign.imageLibrary") }}:</label>
        <div>
            <button
                type="button"
                class="btn btn-sm btn-success"
                :disabled="isUploadingImage || isLoadingImages"
                @click="triggerImageUpload"
            >
                {{ i18n("voucherDesign.upload") }}
            </button>
            <span class="kt-spinner kt-spinner--sm kt-spinner--success ml-1" v-if="isUploadingImage"></span>
        </div>

        <div v-if="isLoadingImages" class="loading-container">
            <span class="kt-spinner kt-spinner--sm kt-spinner--success"></span>
            <span class="ml-2">{{ i18n("voucherDesign.loadingImages") }}</span>
        </div>

        <div v-else-if="libraryImages.length === 0" class="no-images">
            <span class="text-muted">{{ i18n("voucherDesign.noImagesAvailable") }}</span>
        </div>

        <div v-else class="image-grid">
            <div v-for="img in libraryImages" :key="img.name" class="image-container">
                <img
                    :src="img.thumbnailUrl || img.url"
                    :alt="img.alt"
                    class="image-thumb"
                    @mousedown="onMouseDown"
                    @click="insertRealImage(img)"
                />
                <button
                    type="button"
                    class="delete-btn"
                    :disabled="isDeletingImage === img.name"
                    @click.stop="deleteImage(img)"
                    title="Delete image"
                >
                    <i class="fas fa-trash"></i>
                </button>
            </div>
        </div>

        <input ref="imageFileInput" type="file" accept="image/*" style="display: none" @change="onImageSelected" />
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { LocationDao } from "@/data/dao/service/LocationDao";
    import { i18n } from "@/portal/Trans";
    import { addAbsoluteTextBoxWithContent } from "@/portal/components/vouchers/editor/extensions/AbsoluteTextBox";
    import {
        insertContentAtCursor,
        isInsideAbsoluteTextBox,
    } from "@/portal/components/vouchers/editor/helpers/editorUtils";
    import { UiNotification } from "@/portal/ui/Notification";
    import { logError } from "@/shared/logging/errors";
    import type { Editor } from "@tiptap/vue-3";
    import { type Ref, inject, onMounted, ref } from "vue";
    import { translations } from "@/shared/localization/translations";
    import { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { exceptionTypes } from "@/data/util/Config";

    const locationDao = Factory.getInstance<LocationDao>(LocationDao);

    const props = defineProps<{
        editor: Editor;
        locationId: string;
    }>();

    const isUploadingImage = ref(false);
    const isLoadingImages = ref(false);
    const isDeletingImage = ref<string | null>(null);
    const imageFileInput = ref<HTMLInputElement | null>(null);

    type LibraryImage = {
        id: string;
        name: string;
        url: string;
        thumbnailUrl?: string;
        alt: string;
        width: string;
        height: string;
    };

    const libraryImages = ref<LibraryImage[]>([]);
    const isFocusRecentlyLost = inject<Ref<boolean>>("isFocusRecentlyLost");
    const wasFocusRecentlyLostOnMouseDown = ref<boolean>(false);

    function insertRealImage(img: LibraryImage) {
        console.log("insertImageVariable - wasFocusRecentlyLostOnMouseDown", wasFocusRecentlyLostOnMouseDown.value);
        const variableContent = {
            type: "documentImage",
            attrs: {
                src: img.url,
                alt: img.alt ?? "",
                width: img.width ?? "auto",
                height: img.height ?? "auto",
                "data-image-id": img.id,
            },
        };

        console.log("isInsideAbsoluteTextBox(props.editor)", isInsideAbsoluteTextBox(props.editor));
        if (isInsideAbsoluteTextBox(props.editor) && wasFocusRecentlyLostOnMouseDown?.value) {
            console.log("documentImage to absolute text box", img);
            insertContentAtCursor(props.editor, variableContent);
        } else {
            console.log("documentImage to document", img);
            addAbsoluteTextBoxWithContent(props.editor, [
                {
                    type: "paragraph",
                    content: [variableContent],
                },
            ]);
        }
        wasFocusRecentlyLostOnMouseDown.value = false;
    }

    async function onImageSelected(event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;

        try {
            isUploadingImage.value = true;
            await locationDao.uploadVoucherDesignImage(props.locationId, {
                image: file,
            });

            await loadImagesFromApi();
        }
        catch (ex) {
            logError(ex);

            const e = ex as DaoError;
            if (e.type === exceptionTypes.imageLargerThanAllowedException) {
                UiNotification.showException(translations.fileUpload.invalidSize.replace("{{size}}", e.additionalData.allowedSize));
            } else {
                UiNotification.showException(translations.general.failedLoadingData);
            }

        }
        finally {
            isUploadingImage.value = false;
            if (imageFileInput.value) {
                imageFileInput.value.value = "";
            }
        }
    }

    function triggerImageUpload() {
        imageFileInput.value?.click();
    }

    async function deleteImage(img: LibraryImage) {
        UiNotification.confirmWithOptions({
            message: i18n("voucherDesign.reallyDeleteImage", { name: img.alt || img.name }),
            confirmBtn: i18n("general.remove"),
            confirmed: async () => {
                try {
                    isDeletingImage.value = img.name;
                    await locationDao.deleteVoucherDesignImage(props.locationId, img.id);

                    await loadImagesFromApi();
                } catch (error) {
                    logError(error);
                    UiNotification.showException(i18n("voucherDesign.failedDeleteImage"));
                } finally {
                    isDeletingImage.value = null;
                }
            },
        });
    }

    async function loadImagesFromApi() {
        try {
            isLoadingImages.value = true;
            const images = await locationDao.getVoucherDesignImages(props.locationId);

            libraryImages.value = images.map((img, index) => ({
                id: img.id,
                name: `Image_${index + 1}`,
                url: img.imageUrl,
                thumbnailUrl: img.thumbnailUrl,
                alt: `${i18n("voucherDesign.uploadedImage")} ${index + 1}`,
                width: "auto",
                height: "auto",
            }));
        } catch (error) {
            logError(error);
            libraryImages.value = [];
        } finally {
            isLoadingImages.value = false;
        }
    }

    function onMouseDown() {
        window.setTimeout(() => {
            console.log("onMouseDown -ins", isFocusRecentlyLost?.value);
            wasFocusRecentlyLostOnMouseDown.value = isFocusRecentlyLost?.value ?? false;
        }, 10);
    }

    onMounted(() => {
        loadImagesFromApi();
    });
</script>

<style scoped lang="scss">
    .image-library-panel {
        .loading-container {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            color: #6c757d;
        }

        .no-images {
            padding: 1rem 0.5rem;
            color: #6c757d;
        }

        .image-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-top: 0.5rem;

            .image-container {
                position: relative;
                display: inline-block;

                .image-thumb {
                    width: 80px;
                    height: auto;
                    cursor: pointer;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    transition: 0.2s;

                    &:hover {
                        border-color: #8fb52c;
                        box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
                    }
                }

                .delete-btn {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    width: 20px;
                    height: 20px;
                    border: none;
                    border-radius: 50%;
                    background-color: #dc3545;
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    transition: 0.2s;
                    opacity: 0;

                    &:hover {
                        background-color: #c82333;
                        transform: scale(1.1);
                    }

                    &:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }
                }

                &:hover .delete-btn {
                    opacity: 1;
                }
            }
        }
    }
</style>
